import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Palette, Star, Clock, MapPin, Phone, Brush, Users, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import StructuredData from "@/components/StructuredData";
import SEOHead from "@/components/SEOHead";
import FeaturedCarousel from "@/components/FeaturedCarousel";
import FAQ from "@/components/FAQ";
import FloatingActions from "@/components/FloatingActions";
import QuickActions from "@/components/QuickActions";
import heroImage from "@/assets/hero-gallery.jpg";
import valeriaPhoto2 from "@/assets/valeria-wagner-2.png";
import { trackProductView, trackSocialClick } from "@/lib/analytics";

const Index = () => {
  return (
    <>
      <SEOHead
        title="W-Wagner.art - Валерия Вагнер | Художник из Омска | Картины маслом и акрилом"
        description="Профессиональный художник Валерия Вагнер из Омска. Картины маслом и акрилом на заказ, роспись стен. 5 лет опыта. Более 20 довольных клиентов. Художественная мастерская в Омске. Телефон: 8-908-800-57-99"
        keywords={['художник Омск', 'картины маслом Омск', 'живопись Омск', 'купить картину Омск', 'роспись стен Омск', 'Валерия Вагнер', 'заказать картину Омск', 'художественная галерея Омск', 'акрил Омск', 'пейзажи Омск', 'абстракция Омск']}
        canonicalUrl="https://w-wagner.art/"
        ogImage="https://w-wagner.art/hero-gallery.jpg"
      />
      <StructuredData type="gallery" />
      <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div className="absolute inset-0 bg-background/60" />
        
        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6">
            <span className="text-gold">W-Wagner.art</span><br />
            <span className="text-foreground">Валерия Вагнер</span>
          </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto font-light leading-relaxed">
              Профессиональный художник из Омска с 5 лет опыта. Создаю уникальные картины маслом и акрилом в Омске, 
              работаю с росписью стен по всему Омску и Омской области. Авторские работы на заказ в Омске и роспись стен.
            </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="gold-gradient text-black font-medium glow-gold">
              <Link to="/raboty">
                Посмотреть галерею
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/about">О художнике</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Works */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Избранные картины художника из Омска</h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Коллекция уникальных авторских картин маслом и акрилом Валерии Вагнер. Каждая работа создана в художественной мастерской в Омске с особой любовью к искусству и вниманием к деталям. Купить картины в Омске с доставкой.
            </p>
          </div>

          <FeaturedCarousel />

          <div className="text-center">
            <Button asChild variant="outline" size="lg">
              <Link to="/raboty">
                Смотреть все работы
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-transparent to-accent/6" />
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-bold text-gold mb-2">5</div>
              <p className="text-muted-foreground">Лет опыта в Омске</p>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-gold mb-2">20+</div>
              <p className="text-muted-foreground">Выполненных работ</p>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-gold mb-2">20+</div>
              <p className="text-muted-foreground">Довольных клиентов в Омске</p>
            </div>
          </div>
        </div>
      </section>

      {/* Meet the Artist Section */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-card/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium">
                  <Palette className="w-4 h-4" />
                  Знакомство с художником
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">
                  Валерия 
                  <span className="text-primary"> Вагнер</span>
                </h2>
                <p className="text-xl text-muted-foreground leading-relaxed">
                  Профессиональный художник из Омска с 5 летним опытом. Создаю уникальные произведения искусства, 
                  сочетая классические техники с современными подходами в живописи.
                </p>
              </div>
              
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  Специализируюсь на живописи маслом и акрилом, создании росписи стен. 
                  Каждая работа — это воплощение уникальной идеи, созданное с особой любовью 
                  к искусству в моей мастерской в Омске.
                </p>
                
                <div className="flex flex-wrap gap-2">
                  {["Живопись маслом", "Акриловые краски", "Роспись стен", "Пейзажная живопись"].map((skill) => (
                    <span 
                      key={skill}
                      className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <QuickActions showTitle={true} />
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[3/4] rounded-3xl overflow-hidden border border-primary/10">
                <img 
                  src={valeriaPhoto2} 
                  alt="Валерия Вагнер - художник из Омска в своей мастерской с кистями. Профессиональная живопись маслом, акрилом и роспись стен в Омске" 
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="absolute -inset-6 bg-gradient-to-r from-primary/20 to-accent/20 rounded-3xl -z-10 blur-2xl opacity-40" />
              
              
              {/* Floating info card */}
              <div className="absolute -bottom-6 -right-6 bg-card/95 backdrop-blur-sm border border-border/50 rounded-2xl p-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <div>
                    <div className="font-semibold text-sm">Работаю в Омске</div>
                    <div className="text-xs text-muted-foreground">ул. 21 Амурская 37, Амур 2</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview - Redesigned with subtle elegance */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-card/60 via-background to-card/40" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--muted)/0.3),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,hsl(var(--secondary)/0.2),transparent_60%)]" />
        
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-card/80 border border-border/40 text-foreground font-medium mb-6 backdrop-blur-sm">
              <Palette className="w-5 h-5 text-primary" />
              <span className="text-primary">Творческий путь</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Об 
              <span className="text-primary"> искусстве </span>
              и творчестве
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
              Более 5 лет создаю уникальные произведения искусства в Омске, сочетая классические 
              техники живописи с современными подходами. Профессиональная живопись маслом и акрилом в Омске.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="border border-border/50 bg-card/60 backdrop-blur-sm hover:bg-card/80 hover:border-primary/30 transition-all duration-300 group">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                  <Palette className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Масло, акрил</h3>
                <p className="text-sm text-muted-foreground">Профессиональные материалы высшего качества</p>
              </CardContent>
            </Card>

            <Card className="border border-border/50 bg-card/60 backdrop-blur-sm hover:bg-card/80 hover:border-accent/30 transition-all duration-300 group">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-accent/10 flex items-center justify-center group-hover:bg-accent/15 transition-colors">
                  <Star className="w-8 h-8 text-accent" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Роспись стен</h3>
                <p className="text-sm text-muted-foreground">Интерьерная и экстерьерная роспись</p>
              </CardContent>
            </Card>

            <Card className="border border-border/50 bg-card/60 backdrop-blur-sm hover:bg-card/80 hover:border-primary/30 transition-all duration-300 group">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-muted/20 flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                  <Brush className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Авторский стиль</h3>
                <p className="text-sm text-muted-foreground">Уникальная техника и художественное видение</p>
              </CardContent>
            </Card>

            <Card className="border border-border/50 bg-card/60 backdrop-blur-sm hover:bg-card/80 hover:border-accent/30 transition-all duration-300 group">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-secondary/20 flex items-center justify-center group-hover:bg-accent/15 transition-colors">
                  <Clock className="w-8 h-8 text-accent" />
                </div>
                <h3 className="font-semibold text-lg mb-2">5 лет опыта</h3>
                <p className="text-sm text-muted-foreground">Постоянное развитие и совершенствование мастерства</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium shadow-lg hover:shadow-xl transition-all duration-300">
              <Link to="/about">
                <Palette className="w-5 h-5 mr-2" />
                Узнать больше
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Unique Art Process Section */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/6 via-primary/4 to-accent/8" />
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Процесс создания шедевров</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Каждая картина проходит уникальный путь от идеи до воплощения в мастерской в Омске
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="gallery-card text-center">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">1</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Консультация</h3>
                <p className="text-muted-foreground text-sm">
                  Встреча для обсуждения идеи, стиля и ваших пожеланий
                </p>
              </CardContent>
            </Card>
            
            <Card className="gallery-card text-center">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">2</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Эскиз</h3>
                <p className="text-muted-foreground text-sm">
                  Создание предварительного эскиза и выбор цветовой палитры
                </p>
              </CardContent>
            </Card>
            
            <Card className="gallery-card text-center">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">3</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Подготовка</h3>
                <p className="text-muted-foreground text-sm">
                  Подготовка холста и материалов высшего качества
                </p>
              </CardContent>
            </Card>
            
            <Card className="gallery-card text-center">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">4</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Создание</h3>
                <p className="text-muted-foreground text-sm">
                  Профессиональное выполнение работы в мастерской
                </p>
              </CardContent>
            </Card>
            
            <Card className="gallery-card text-center">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">5</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Финиширование</h3>
                <p className="text-muted-foreground text-sm">
                  Защитное покрытие, обработка и подготовка к доставке
                </p>
              </CardContent>
            </Card>
            
            <Card className="gallery-card text-center">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">6</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Доставка</h3>
                <p className="text-muted-foreground text-sm">
                  Доставка готовой работы или самовывоз из мастерской
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <Button asChild size="lg" className="gold-gradient text-black font-medium">
              <Link to="/services">
                Заказать картину
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Частые вопросы о заказе картин в Омске</h2>
            <p className="text-lg text-muted-foreground">
              Ответы на самые популярные вопросы о работе с художником в Омске
            </p>
          </div>
          <FAQ />
        </div>
      </section>

      {/* Contact CTA - Redesigned */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-card/80 via-background to-muted/40" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,hsl(var(--primary)/0.1),transparent_70%)]" />
        
        <div className="relative max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium mb-6">
              <Phone className="w-4 h-4" />
              Свяжитесь с нами
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Создадим вашу 
              <span className="text-primary"> мечту</span> вместе
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
              Обсудим ваши идеи, подберем стиль и создадим уникальное произведение искусства специально для вас
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="border border-border/50 bg-card/60 backdrop-blur-sm hover:bg-card/80 hover:border-primary/30 transition-all duration-300 group">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                  <MapPin className="w-8 h-8 text-primary" />
                </div>
                <h4 className="font-semibold text-lg mb-2">Мастерская в Омске</h4>
                <p className="text-sm text-muted-foreground mb-2">ул. 21 Амурская 37, Амур 2</p>
                <p className="text-xs text-muted-foreground">Приходите посмотреть работы</p>
              </CardContent>
            </Card>

            <Card className="border border-border/50 bg-card/60 backdrop-blur-sm hover:bg-card/80 hover:border-accent/30 transition-all duration-300 group">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-accent/10 flex items-center justify-center group-hover:bg-accent/15 transition-colors">
                  <Phone className="w-8 h-8 text-accent" />
                </div>
                <h4 className="font-semibold text-lg mb-2">Прямая связь</h4>
                <a href="tel:+79088005799" className="text-lg font-medium text-primary hover:text-primary/80 transition-colors block mb-1">
                  +7 (908) 800-57-99
                </a>
                <p className="text-xs text-muted-foreground">Ответим на все вопросы</p>
              </CardContent>
            </Card>

            <Card className="border border-border/50 bg-card/60 backdrop-blur-sm hover:bg-card/80 hover:border-primary/30 transition-all duration-300 group">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-secondary/20 flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                  <Clock className="w-8 h-8 text-primary" />
                </div>
                <h4 className="font-semibold text-lg mb-2">График работы</h4>
                <div className="text-sm text-muted-foreground">
                  <p>Пн-Пт: 9:00-18:00</p>
                  <p className="text-xs mt-1">Сб-Вс: по договоренности</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center space-y-6">
            <Button asChild size="lg" className="gold-gradient text-black font-medium shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
              <Link to="/contacts">
                <Palette className="w-5 h-5 mr-2" />
                Обсудить проект
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
            
            <div className="flex items-center justify-center gap-4">
              <a 
                href="https://wa.me/79507818785" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => trackSocialClick('WhatsApp', 'главная страница')}
                className="flex items-center justify-center w-12 h-12 rounded-full bg-green-500/10 hover:bg-green-500/20 transition-colors group"
              >
                <svg className="w-6 h-6 text-green-600 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
              </a>
              <a 
                href="https://t.me/FallenSonS"
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-500/10 hover:bg-blue-500/20 transition-colors group"
              >
                <svg className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
              </a>
              <a 
                href="https://vk.com/im?sel=-214021218" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => trackSocialClick('VK Сообщения', 'main_page')}
                className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-400/10 hover:bg-blue-400/20 transition-colors group"
              >
                <svg className="w-6 h-6 text-blue-500 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.408 0 15.684 0zM12 18c-3.314 0-6-2.686-6-6s2.686-6 6-6 6 2.686 6 6-2.686 6-6 6zm6-10.5c-.828 0-1.5-.672-1.5-1.5s.672-1.5 1.5-1.5 1.5.672 1.5 1.5-.672 1.5-1.5 1.5z"/>
                  <circle cx="12" cy="12" r="3" fill="white"/>
                  <text x="12" y="8" textAnchor="middle" fontSize="4" fill="currentColor">VK</text>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>
      
      <FloatingActions currentPage="index" />
    </div>
    </>
  );
};

export default Index;